<template>
    <!-- ── Login Modal ── -->
    <div class="pub-modal-overlay" :class="{ open: loginModalOpen }" @click.self="$emit('close-login')">
      <div class="pub-modal-box">
        <div class="pub-modal-header">
          <h5 class="pub-modal-title">Sign In to Continue</h5>
          <button class="pub-modal-close" @click="$emit('close-login')">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <div class="pub-modal-body">
          <p class="pub-modal-sub">Sign in to your account to complete your subscription.</p>
          <div v-if="validationErrors.length" class="pub-error-box">
            <div v-for="(err, i) in validationErrors" :key="i">{{ err }}</div>
          </div>
          <form @submit.prevent="$emit('login')">
            <div class="form-group-pub">
              <label class="form-label-pub">Email Address</label>
              <input
                type="email"
                :value="login.email"
                @input="$emit('update:login', { ...login, email: $event.target.value })"
                class="form-input-pub"
                placeholder="you@example.com"
                required
              />
            </div>
            <div class="form-group-pub" style="margin-bottom:0;">
              <label class="form-label-pub">Password</label>
              <input
                type="password"
                :value="login.password"
                @input="$emit('update:login', { ...login, password: $event.target.value })"
                class="form-input-pub"
                placeholder="••••••••"
                required
              />
            </div>
          </form>
        </div>
        <div class="pub-modal-footer">
          <button class="pub-btn-ghost" @click="$emit('close-login')">Cancel</button>
          <button class="pub-btn-primary" :disabled="loginLoading" @click="$emit('login')">
            <font-awesome-icon v-if="loginLoading" icon="spinner" spin />
            {{ loginLoading ? 'Signing in...' : 'Sign In' }}
          </button>
        </div>
      </div>
    </div>
  
    <!-- ── Payment Modal ── -->
    <div class="pub-modal-overlay" :class="{ open: paymentModalOpen }" @click.self="$emit('close-payment')">
      <div class="pub-modal-box pub-modal-box--lg">
        <div class="pub-modal-header">
          <div>
            <h5 class="pub-modal-title">{{ currentData?.name }}</h5>
            <p class="pub-modal-sub" style="margin:0;">
              {{ currencySymbol }}{{ formatCurrency(currentData?.amount) }} / {{ currentData?.type }}
            </p>
          </div>
          <button class="pub-modal-close" @click="$emit('close-payment')">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <div class="pub-modal-body">
          <p class="payment-instruction">Choose your preferred payment channel to complete your subscription.</p>
  
          <!-- Local (NG) -->
          <div v-if="countryCode === 'NG'" class="payment-channels">
            <div v-for="ch in localPaymentChannels" :key="ch.id" class="payment-channel-card">
              <img :src="backendUrl + 'images/payments/' + ch.logo" :alt="ch.name" class="channel-logo" />
              <div class="channel-name">{{ ch.name }}</div>
              <button
                class="channel-btn"
                @click="ch.slug === 'flutter'
                  ? $emit('flutterwave', { data: currentData, channel: ch })
                  : $emit('paystack',    { data: currentData, channel: ch })"
              >
                Pay with {{ ch.name }}
              </button>
            </div>
          </div>
  
          <!-- International -->
          <div v-else class="payment-channels">
            <div v-for="ch in internationalPaymentChannels" :key="ch.id" class="payment-channel-card">
              <img :src="backendUrl + 'images/payments/' + ch.logo" :alt="ch.name" class="channel-logo" />
              <div class="channel-name">{{ ch.name }}</div>
              <button class="channel-btn" @click="$emit('stripe', { data: currentData, channel: ch })">
                Pay with {{ ch.name }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  
    <!-- Backdrop -->
    <div v-if="loginModalOpen || paymentModalOpen" class="pub-backdrop"></div>
  </template>
  
  <script>
  export default {
    name: 'PricingModals',
  
    props: {
      loginModalOpen:              { type: Boolean, default: false },
      paymentModalOpen:            { type: Boolean, default: false },
      currentData:                 { type: Object,  default: null  },
      login:                       { type: Object,  required: true },
      validationErrors:            { type: Array,   default: () => [] },
      loginLoading:                { type: Boolean, default: false },
      countryCode:                 { type: String,  default: 'GB'  },
      currencySymbol:              { type: String,  default: '£'   },
      localPaymentChannels:        { type: Array,   default: () => [] },
      internationalPaymentChannels:{ type: Array,   default: () => [] },
      backendUrl:                  { type: String,  default: ''    },
    },
  
    emits: [
      'close-login', 'close-payment', 'login', 'update:login',
      'flutterwave', 'paystack', 'stripe',
    ],
  
    methods: {
      formatCurrency(amount) {
        amount = Number(amount) || 0;
        if (this.countryCode === 'NG') return (amount * 2000).toLocaleString();
        return amount.toLocaleString();
      },
    },
  };
  </script>
  
  <style scoped>
  .pub-modal-overlay   { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
  .pub-modal-overlay.open { opacity:1; pointer-events:all; }
  .pub-backdrop        { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.5); backdrop-filter:blur(3px); }
  .pub-modal-box       { background:#fff; border-radius:16px; width:100%; max-width:460px; margin:16px; box-shadow:0 24px 64px rgba(0,0,0,.18); overflow:hidden; transform:translateY(20px); transition:transform .25s ease; z-index:1051; position:relative; }
  .pub-modal-box--lg   { max-width:640px; }
  .pub-modal-overlay.open .pub-modal-box { transform:translateY(0); }
  .pub-modal-header    { padding:22px 24px 18px; border-bottom:1px solid #f0f4f8; display:flex; align-items:center; justify-content:space-between; }
  .pub-modal-title     { font-size:17px; font-weight:700; color:#1a2b45; margin:0; }
  .pub-modal-sub       { font-size:13px; color:#7a90b0; margin:0 0 16px; }
  .pub-modal-close     { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; }
  .pub-modal-close:hover { background:#f4f7fb; }
  .pub-modal-body      { padding:22px 24px; }
  .pub-modal-footer    { padding:16px 24px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }
  .pub-btn-ghost       { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; }
  .pub-btn-ghost:hover { background:#e4eaf2; }
  .pub-btn-primary     { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; border:none; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .pub-btn-primary:hover:not(:disabled) { background:#003b8e; }
  .pub-btn-primary:disabled { opacity:.5; cursor:not-allowed; }
  .pub-error-box { background:#fff5f5; border-left:3px solid #e03535; border-radius:0 6px 6px 0; padding:10px 14px; margin-bottom:14px; font-size:13px; color:#c02828; }
  .form-group-pub   { display:flex; flex-direction:column; gap:6px; margin-bottom:16px; }
  .form-label-pub   { font-size:13px; font-weight:600; color:#1a2b45; }
  .form-input-pub   { width:100%; padding:10px 14px; border:1px solid #dde5f0; border-radius:8px; font-size:14px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s; font-family:inherit; }
  .form-input-pub:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .payment-instruction { font-size:14px; color:#6b7a99; margin-bottom:20px; }
  .payment-channels    { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; }
  .payment-channel-card { border:1px solid #e4eaf2; border-radius:12px; padding:20px; display:flex; flex-direction:column; align-items:center; gap:12px; text-align:center; }
  .channel-logo { max-width:120px; max-height:40px; object-fit:contain; }
  .channel-name { font-size:14px; font-weight:600; color:#1a2b45; }
  .channel-btn  { width:100%; padding:9px 0; border-radius:8px; border:none; background:#004AAD; color:#fff; font-size:13px; font-weight:600; cursor:pointer; transition:background .15s; }
  .channel-btn:hover { background:#003b8e; }
  @media (max-width:480px) { .payment-channels { grid-template-columns:1fr; } }
  </style>